import React, { useCallback, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

import AdCopyStudio, { StudioHostData } from 'pages/shared/adCopyStudio/AdCopyStudio';
import { GenerationRow, GroupInfo } from './types';

// Launch copy attached to a creative: up to 5 headlines and 5 captions,
// saved on the generation row. Suggestions come from the shared Ad Copy
// Studio — winners-grounded and fully inspectable — with this panel as its
// first host.
//
// Pass `group` and the same panel edits the GROUP's shared copy instead: it
// seeds from and saves to the group, while the studio still runs against the
// passed row (a group has no source ad or product of its own — its first
// member supplies both).

const MAX_ITEMS = 5;

interface AdCopyPanelProps {
  row: GenerationRow;
  service: StaticAdsService;
  onSaved: () => void;
  /** Present when this panel edits a group's shared copy rather than the row's. */
  group?: GroupInfo;
  /**
   * The resolved label of a structured run's format. Passed in rather than
   * looked up here: the caller already holds the registry, and 8 of the 15 keys
   * do not reduce to their label.
   */
  formatLabel?: string | null;
}

function parseAdCopy(raw: GenerationRow['adCopy']): { headlines: string[]; captions: string[] } {
  const value = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  return {
    headlines: Array.isArray(value?.headlines) ? value!.headlines.slice(0, MAX_ITEMS) : [],
    captions: Array.isArray(value?.captions) ? value!.captions.slice(0, MAX_ITEMS) : [],
  };
}

function parseVariantArray(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') { try { const p = JSON.parse(raw); return Array.isArray(p) ? p.map(String) : []; } catch { return []; } }
  return [];
}

function ListEditor({ label, items, onChange, multiline }: {
  label: string; items: string[]; onChange: (next: string[]) => void; multiline?: boolean;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{label} ({items.length}/{MAX_ITEMS})</Typography>
      <Stack spacing={0.75}>
        {items.map((value, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
            <TextField
              size="small" fullWidth multiline={multiline} maxRows={8} value={value}
              onChange={e => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
            />
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => { void navigator.clipboard.writeText(value); }}>
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => onChange(items.filter((_, j) => j !== i))}>
              ×
            </IconButton>
          </Stack>
        ))}
        {items.length < MAX_ITEMS && (
          <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => onChange([...items, ''])}>
            Add {label.toLowerCase().replace(/s$/, '')}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default function AdCopyPanel({ row, service, onSaved, group, formatLabel = null }: AdCopyPanelProps) {
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [captions, setCaptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when a different generation opens — or, in group mode, a different
  // group. Keying on the groupId matters: stepping between two members of one
  // stack must NOT re-seed, because they share one set of copy and a re-seed
  // would throw away unsaved edits made from the sibling.
  //
  // But one group's copy is edited from TWO panels (the group dialog and each
  // member's detail dialog), so keying on identity alone was too coarse: copy
  // saved from one was invisible to the other, which then overwrote it on its
  // next save. So we also re-seed when the incoming copy no longer matches
  // what we last seeded — unless this panel is dirty, in which case the user's
  // in-progress edits win over a sibling's refresh. Stepping between members
  // still doesn't re-seed, because there the incoming value is unchanged.
  const seededFor = useRef<string | null>(null);
  const seededSnapshot = useRef<string>('');
  const seedKey = group ? group.groupId : row.generationId;
  const incoming = parseAdCopy(group ? group.adCopy : row.adCopy);
  const incomingSnapshot = JSON.stringify(incoming);
  const dirty = JSON.stringify({ headlines, captions }) !== seededSnapshot.current;
  if (seededFor.current !== seedKey || (incomingSnapshot !== seededSnapshot.current && !dirty)) {
    seededFor.current = seedKey;
    seededSnapshot.current = incomingSnapshot;
    setHeadlines(incoming.headlines);
    setCaptions(incoming.captions);
    setError(null);
  }

  // Where a save lands: the group's shared copy, or this one generation's.
  const groupId = group?.groupId;
  const persist = useCallback(async (body: { headlines: string[]; captions: string[] }) => {
    const saved = groupId ? await service.saveGroupAdCopy(groupId, body) : await service.saveAdCopy(row.generationId, body);
    // What we just wrote becomes the seed: the refetch that follows carries our
    // own save back, and that must not read as a sibling's change — while a
    // real sibling edit still does.
    seededSnapshot.current = JSON.stringify(body);
    return saved;
  }, [groupId, row.generationId, service]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await persist({ headlines, captions });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The studio's host adapter: distilled inputs only.
  const loadHostData = useCallback(async (): Promise<StudioHostData> => {
    // A structured run has no source ad, so there is no analysis to fetch.
    const detail = row.staticAdId
      ? await service.getAd(row.staticAdId).catch(() => null)
      : null;
    const f = detail?.features;
    // Without a source ad the studio would otherwise be handed an empty analysis
    // and quietly write weaker copy for it. Give it what this run actually HAS —
    // the format it was built on and the direction it was given — rather than
    // nothing at all.
    const structuredAnalysis = [
      formatLabel && `Format: ${formatLabel}`,
      row.remakeBrief && `Direction: ${row.remakeBrief}`,
    ].filter(Boolean).join('\n');
    const analysis = f ? [
      f.hookSummary && `Hook: ${f.hookSummary}`,
      f.claimSummary && `Claim: ${f.claimSummary}`,
      f.proofSummary && `Proof: ${f.proofSummary}`,
      f.coreBenefit && `Core benefit: ${f.coreBenefit}`,
      f.uniqueAngle && `Unique angle: ${f.uniqueAngle}`,
      f.persuasionAngle && `Persuasion: ${f.persuasionAngle}`,
      f.awarenessLevel && `Audience awareness: ${f.awarenessLevel}`,
      f.tone && `Tone: ${f.tone}`,
    ].filter(Boolean).join('\n') : structuredAnalysis;

    // The full product catalog of selling points, with the subset chosen for
    // THIS generation run pre-checked — the run's picks are a starting
    // selection, not a hard filter.
    let points = parseVariantArray(row.sellingPoints);
    let pointsChecked: boolean[] | undefined;
    if (row.productId && /^\d+$/.test(row.productId)) {
      const resp = await service.getSellingPoints(Number(row.productId)).catch(() => null);
      const all = (resp?.sellingPoints || []).slice(0, 12);
      if (all.length) {
        const chosen = new Set(points.map(t => t.trim().toLowerCase()));
        pointsChecked = all.map(t => (chosen.size ? chosen.has(t.trim().toLowerCase()) : true));
        points = all;
      }
    }

    return {
      product: row.productName,
      sellingPoints: points,
      sellingPointsChecked: pointsChecked,
      analysis,
      originalHeadline: row.sourceHeadline || detail?.ad?.headline || '',
      originalPrimaryText: row.sourceCaption || detail?.ad?.caption || '',
    };
  }, [row, service, formatLabel]);

  // Past studio runs are grouped by the SOURCE AD server-side, so a sibling
  // generation of the same ad shares the history.
  const loadHistory = useCallback(
    () => service.listAdCopyRuns(row.generationId).then(resp => resp.runs),
    [row.generationId, service],
  );

  // Product-wide prior copy (raw responses) for the anti-repetition card.
  const loadPriorCopy = useCallback(
    () => service.listAdCopyRuns(row.generationId, { scope: 'product' })
      .then(resp => resp.runs.map(r => r.responseText || '').filter(Boolean)),
    [row.generationId, service],
  );

  const deleteRun = useCallback(
    (runId: string) => service.deleteAdCopyRun(row.generationId, runId),
    [row.generationId, service],
  );

  // A fresh studio generation replaces the slots wholesale (capped at
  // MAX_ITEMS, which the studio is told so it can confirm the real count) —
  // the user edits from a filled panel rather than clicking Use ten times.
  // The explicit "Use All" action also persists (save: true), so the copy is
  // on the row without a separate Save click.
  const applyAll = useCallback((newHeadlines: string[], newPrimaryTexts: string[], opts?: { save?: boolean }) => {
    // An empty list means the run produced nothing of that kind (a truncated
    // or unparsable response) — never the user asking for it to be cleared.
    // Letting it through would wipe copy already saved on the row.
    const nextHeadlines = newHeadlines.length ? newHeadlines.slice(0, MAX_ITEMS) : headlines;
    const nextCaptions = newPrimaryTexts.length ? newPrimaryTexts.slice(0, MAX_ITEMS) : captions;
    setHeadlines(nextHeadlines);
    setCaptions(nextCaptions);
    if (opts?.save) {
      setSaving(true);
      setError(null);
      persist({ headlines: nextHeadlines, captions: nextCaptions })
        .then(() => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2500);
          onSaved();
        })
        .catch((err: any) => setError(err?.response?.data?.error || err?.message || 'Save failed'))
        .finally(() => setSaving(false));
    }
  }, [persist, onSaved, headlines, captions]);

  // Slot access for the studio's pick-one-to-replace dialog.
  const getSlots = useCallback(
    (kind: 'headline' | 'primaryText') => (kind === 'headline' ? headlines : captions),
    [headlines, captions],
  );
  const replaceSlot = useCallback((kind: 'headline' | 'primaryText', index: number, text: string) => {
    const setList = kind === 'headline' ? setHeadlines : setCaptions;
    setList(prev => prev.map((v, i) => (i === index ? text : v)));
  }, []);

  // Returns whether the item landed — false means all slots are full, and the
  // studio surfaces that instead of silently doing nothing.
  const addTo = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>) => (text: string): boolean => {
    const emptyIdx = list.findIndex(v => !v.trim());
    if (emptyIdx !== -1) {
      setList(list.map((v, i) => (i === emptyIdx ? text : v)));
      return true;
    }
    if (list.length < MAX_ITEMS) {
      setList([...list, text]);
      return true;
    }
    return false;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2">Ad copy</Typography>

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <ListEditor label="Headlines" items={headlines} onChange={setHeadlines} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <ListEditor label="Captions" items={captions} onChange={setCaptions} multiline />
          </Box>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => setStudioOpen(true)}>
            Ad Copy Studio
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : savedFlash ? 'Saved ✓' : 'Save copy'}
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      <AdCopyStudio
        open={studioOpen}
        onClose={() => setStudioOpen(false)}
        title={`Ad Copy Studio — ${row.productName || 'creative'}`}
        maxItems={MAX_ITEMS}
        loadHostData={loadHostData}
        loadHistory={loadHistory}
        loadPriorCopy={loadPriorCopy}
        deleteRun={deleteRun}
        generate={(prompt, model) => service.suggestAdCopy(row.generationId, { prompt, model })}
        onUseHeadline={addTo(headlines, setHeadlines)}
        onUsePrimaryText={addTo(captions, setCaptions)}
        onApplyAll={applyAll}
        getSlots={getSlots}
        replaceSlot={replaceSlot}
      />
    </Box>
  );
}
